import React, { useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import ReviewsModal from '@rover/components/src/cards/SearchResultCard/ReviewsModal';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import AnimatedSpinner from '@rover/components/src/typography/AnimatedSpinner';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import MQ from '@rover/kibble/styles/MediaQuery';
import Review from '@rover/react-lib/src/components/modals/Review/Review';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import { ApiPersonDetailReviewsListPetType as PetTypeReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import emitMetric from '@rover/utilities/emitMetric';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { OwnerConversionMemberProfileReadMoreReviewsEvent } from '../../analyticsEvents';
import { ReviewsLayout } from '../../SitterProfileLayout';
import { getInitialReviewsParams } from '../../utils/utils';

import AutomaticRecordContent from './AutomaticRecordContent/AutomaticRecordContent';
import NoReviewsBox from './NoReviewsBox';
import PetTypeReviewFilter from './PetTypeReviewFilter';

type Props = {
  opk: string;
  sitterAvatar: string;
};

// The Button component doesn't accept responsive fullWidth prop
// eslint-disable-next-line rover/prefer-kibble-components
const StyledButton = styled(Button)`
  ${MQ.XS.toString()} {
    width: 100%;
  }
`;

const ReviewsSection = ({ opk, sitterAvatar }: Props): JSX.Element | null => {
  const { t } = useLingui();
  const availablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePets);

  const initialReviewsParams = getInitialReviewsParams();

  // Pet type filter state - default to 'cat' for Cat in a Flat, null for others
  const [petTypeFilter, setPetTypeFilter] = useState<
    (typeof PetTypeReview)[keyof typeof PetTypeReview] | null
  >(isCatOnlyBrand ? PetTypeReview.cat : null);

  const { data: reviews, isLoading: isLoadingReviews } = useApiPersonDetailReviewsList(opk, {
    ...initialReviewsParams,
    ...(petTypeFilter && { petType: petTypeFilter }),
  });
  const [scrollIndex, setScrollIndex] = useState<number>(-1);
  const [seeMoreButtonClick, setSeeMoreButtonClick] = useState<boolean>(false);
  const [automaticRecordModal, setAutomaticRecordModal] = useState<boolean>(false);

  const reviewListSummary = (reviews?.results || []).slice(0, 4);
  const margins: SpacingSystemType = ['0x', '6x', '0x', '6x'];

  const closeModal = (): void => setScrollIndex(-1);
  const toggleAutomaticRecordModal = (): void => setAutomaticRecordModal(!automaticRecordModal);

  const handleReadAll = ({ webEvent }: { webEvent?: React.MouseEvent<HTMLElement> }): void => {
    setSeeMoreButtonClick(true);
    setScrollIndex(0);
    emitMetric('sitter_profile_read_all_reviews_clicked');
    Logger.info('sitter_profile_read_all_reviews_clicked');
    emitAnalyticsEvent(new OwnerConversionMemberProfileReadMoreReviewsEvent({ providerId: opk }));
    webEvent?.currentTarget.blur();
  };

  const handleFilterChange = (
    filter: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null
  ): void => {
    setPetTypeFilter(filter);
    emitMetric('sitter_profile_review_filter_changed', { filter: filter || 'all' });
    Logger.info('sitter_profile_review_filter_changed', { filter: filter || 'all' });
  };

  const reviewTitle = (
    <Heading size="400" fontWeight="semibold">
      <Box sx={{ fontSize: '26px' }}>{t`Reviews`}</Box>
    </Heading>
  );

  if (reviews?.count === 0) return null;

  const reviewsSummary = (
    <Flex width="100%" flexDirection={['column', 'row', 'column', 'row']} flexWrap="wrap">
      {reviewListSummary.map((review, index) => (
        <Flex
          width={['100%', '50%', '100%', '50%']}
          key={review.url}
          pr={index % 2 === 0 ? margins : '0x'}
          pl={index % 2 === 0 ? '0x' : margins}
          pb="10x"
        >
          <Review
            review={review}
            onReadMore={() => setScrollIndex(index)}
            onLearnMore={toggleAutomaticRecordModal}
          />
        </Flex>
      ))}
    </Flex>
  );

  const reviewsContent =
    reviewListSummary.length === 0 ? (
      <NoReviewsBox sx={{ marginBottom: '40px' }} />
    ) : (
      reviewsSummary
    );

  const modalOpen = scrollIndex >= 0;

  return (
    <ReviewsLayout>
      <Box width="100%" data-testid="reviews-list">
        <Flex
          width="100%"
          flexDirection={['column', 'row', 'column', 'row']}
          justifyContent={['initial', 'space-between', 'initial', 'space-between']}
          gap="3x"
          pb={['8x', '12x', '8x', '12x']}
          pt={['4x', '0x', '4x', '0x']}
        >
          {reviewTitle}{' '}
          <PetTypeReviewFilter
            onChange={handleFilterChange}
            initialPetType={isCatOnlyBrand ? PetTypeReview.cat : undefined}
          />
        </Flex>
        {isLoadingReviews ? (
          <Flex width="100%" justifyContent="center" pb={['8x', '12x', '8x', '12x']}>
            <AnimatedSpinner />
          </Flex>
        ) : (
          reviewsContent
        )}
        <StyledButton
          onPress={handleReadAll}
          sx={{
            '&:focus': {
              backgroundColor: 'initial',
            },
          }}
        >
          {t`Read more reviews`}
        </StyledButton>
        <ReviewsModal
          opk={opk}
          modalOpen={modalOpen}
          closeModal={closeModal}
          avatarUrl={sitterAvatar}
          scrollIndex={scrollIndex}
          petTypeFilter={petTypeFilter}
          onFilterChange={isCatOnlyBrand ? handleFilterChange : undefined}
          title={t`Reviews`}
          seeMoreButtonClick={seeMoreButtonClick}
          setSeeMoreButtonClick={setSeeMoreButtonClick}
        />
        <Modal isOpen={automaticRecordModal && !modalOpen} onClose={toggleAutomaticRecordModal}>
          <AutomaticRecordContent />
        </Modal>
      </Box>
    </ReviewsLayout>
  );
};

export default ReviewsSection;
